import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Table from '../shared/table/Table';
import { FiEye, FiEdit3, FiTrash2 } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import useTranslations from '../../hooks/useTranslations';
import useUserLanguage from '../../hooks/useUserLanguage';
import { confirmAlert } from "react-confirm-alert"; 
import { toast } from "react-toastify"; 

const VersionTable = () => {
  const [versions, setVersions] = useState([]);
  const navigate = useNavigate();

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const t = useMemo(() => (key, def) => translations[key] || def, [translations]);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const list = Array.isArray(res.data) ? res.data : res.data.data || [];
      const activeVersions = list.filter(v => !v.deleted_at); // 🔥 Silinmemişleri filtrele
      setVersions(activeVersions);
    } catch (err) {
      console.error('❌ Versiyonlar alınamadı:', err);
    }
  };

  const handleSoftDelete = (id) => {
    confirmAlert({
      title: t("SOFT_DELETE_CONFIRM_TITLE", "Versiyonu Çöp Kutusuna Taşı"),
      message: t("SOFT_DELETE_CONFIRM_MSG", "Bu versiyonu çöpe taşımak istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_SOFT_DELETE", "Evet, Taşı"),
          onClick: async () => {
            try {
              const token = localStorage.getItem('token');
              await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/versions/soft-delete/${id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success(t("SUCCESS_VERSION_SOFT_DELETED", "Versiyon çöpe taşındı!"));
              setVersions(prev => prev.filter(v => v.id !== id));
            } catch (error) {
              console.error("❌ Versiyon çöpe taşınamadı:", error);
              toast.error(t("ERROR_VERSION_SOFT_DELETE_FAILED", "Çöpe taşıma başarısız!"));
            }
          },
        },
        {
          label: t("CANCEL", "İptal"),
          onClick: () => toast.info(t("SOFT_DELETE_CANCELLED", "İşlem iptal edildi.")),
        },
      ],
    });
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: () => t('VERSION_ID', 'ID'),
      meta: { headerClassName: 'text-start', className: 'text-start' },
      size: 60,
    },
    {
      accessorKey: 'title',
      header: () => t('VERSION_TITLE', 'Başlık'),
      meta: { headerClassName: 'text-start', className: 'text-start' },
      size: 300,
    },
    {
      accessorKey: 'created_at',
      header: () => t('VERSION_CREATED_AT', 'Oluşturma Tarihi'),
      meta: { headerClassName: 'text-end', className: 'text-end date-column' },
      size: 180,
      cell: (info) => {
        const rawDate = info.getValue();
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString('tr-TR') : "-";
        return (
          <div style={{ textAlign: 'end', width: '125px' }}>
            {formattedDate}
          </div>
        );
      }
    },
    {
      accessorKey: 'updated_at',
      header: () => t('VERSION_UPDATED_AT', 'Güncelleme Tarihi'),
      meta: { headerClassName: 'text-end', className: 'text-end date-column' },
      size: 180,
      cell: (info) => {
        const rawDate = info.getValue();
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString('tr-TR') : "-";
        return (
          <div style={{ textAlign: 'end', width: '125px' }}>
            {formattedDate}
          </div>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: () => (
        <div className="text-end pe-3">{t('TABLE_ACTIONS_LABEL', 'İşlemler')}</div>
      ),
      enableSorting: false,
      meta: { headerClassName: 'text-end', className: 'text-end' },
      size: 130,
      cell: (info) => {
        const id = info.row.original.id;
        return (
          <div className="d-flex justify-content-end gap-2 pe-3">
            <Link
              to={`/version/view/${id}`}
              className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
              title={t("VIEW", "Görüntüle")}
            >
              <FiEye />
            </Link>
            <Link
              to={`/version/edit/${id}`}
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              title={t('EDIT', 'Düzenle')}
            >
              <FiEdit3 />
            </Link>
            <button
              onClick={() => handleSoftDelete(id)}
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
              title={t('SOFT_DELETE', 'Çöp Kutusuna Taşı')}
            >
              <FiTrash2 />
            </button>
          </div>
        );
      }
    }
  ], [t]);

  if (!versions.length) return <p>{t('LOADING', 'Yükleniyor…')}</p>;

  return <Table data={versions} columns={columns} />;
};

export default VersionTable;
